import React, { useContext, memo, useMemo } from 'react';
import { useAdmin } from '../AdminContext';
import { PageContext } from '../PageContext';
import { Notification, Page, UserProfile } from '../types';
import { useAuth } from '../AuthContext';

// FIX: Changed notification.user to notification.fromUser
const NotificationItem: React.FC<{ notification: Notification, onProfileClick: (id?: string) => void, onNotificationClick: (notification: Notification) => void }> = memo(({ notification, onProfileClick, onNotificationClick }) => {
    
    // Determine custom visual style or icon based on notification type
    const getNotificationVisuals = () => {
        switch(notification.type) {
            case 'leaderboard_pass':
                return { iconClass: 'ph-trophy text-yellow-500', bgClass: 'bg-yellow-100' };
            case 'guess_received':
                return { iconClass: 'ph-brain text-purple-500', bgClass: 'bg-purple-100' };
            case 'guess_correct':
                return { iconClass: 'ph-check-circle text-green-500', bgClass: 'bg-green-100' };
            case 'guess_incorrect':
                return { iconClass: 'ph-x-circle text-red-500', bgClass: 'bg-red-100' };
            case 'new_message':
                return { iconClass: 'ph-chat-circle-text text-blue-500', bgClass: 'bg-blue-100' };
            case 'post_under_review':
                return { iconClass: 'ph-warning text-orange-500', bgClass: 'bg-orange-100' };
            case 'group_invite':
                return { iconClass: 'ph-envelope-open text-pink-500', bgClass: 'bg-pink-100' };
            default:
                return null; // Fallback to user avatar
        }
    };

    const visuals = getNotificationVisuals();

    return (
        <div onClick={() => onNotificationClick(notification)} className={`p-3 rounded-lg flex items-start space-x-3 cursor-pointer transition-colors ${notification.read ? 'bg-gray-100' : 'bg-white shadow-sm hover:bg-gray-50'}`}>
            <div className="relative flex-shrink-0">
                {/* Use specific icon if available, otherwise user avatar */}
                {visuals && !notification.fromUser ? (
                    <div className={`w-10 h-10 rounded-full flex items-center justify-center ${visuals.bgClass}`}>
                        <i className={`ph-duotone ${visuals.iconClass} text-xl`}></i>
                    </div>
                ) : (
                    notification.fromUser?.avatar && <img src={notification.fromUser.avatar} alt={notification.fromUser.name} onClick={(e) => { e.stopPropagation(); onProfileClick(notification.fromUser?.id);}} className="w-10 h-10 rounded-full object-cover" />
                )}
                {!notification.read && <span className="absolute -top-1 -right-1 block h-3 w-3 rounded-full bg-[var(--accent-pink)] border-2 border-white"></span>}
            </div>
            <div className="flex-1">
                <p className="text-sm text-gray-800">
                    {notification.fromUser && (
                        <span className="font-bold cursor-pointer hover:underline mr-1" onClick={(e) => { e.stopPropagation(); onProfileClick(notification.fromUser?.id);}}>
                            {notification.fromUser.name}
                        </span>
                    )}
                    {notification.text}
                </p>
                <p className="text-xs text-gray-400 mt-1">{notification.time.toDate().toLocaleString()}</p>
            </div>
            {notification.puzzle && <img src={notification.puzzle.imageUrl} alt="puzzle thumbnail" className="w-12 h-12 rounded-md object-cover flex-shrink-0" />}
        </div>
    );
});

// Helper for Friend Request & Group Invites
const RequestItem: React.FC<{ 
    notification: Notification, 
    onAccept: (e: React.MouseEvent) => void, 
    onDecline: (e: React.MouseEvent) => void, 
    onProfileClick: (id?: string) => void,
    type: 'friend' | 'group'
}> = memo(({ notification, onAccept, onDecline, onProfileClick, type }) => {
    
    return (
        <div className="bg-white p-3 rounded-lg shadow-sm mb-2">
            <div className="flex items-start space-x-3">
                 <div className="relative flex-shrink-0">
                    <img src={notification.fromUser?.avatar} alt={notification.fromUser?.name} onClick={() => onProfileClick(notification.fromUser?.id)} className="w-10 h-10 rounded-full object-cover cursor-pointer" />
                </div>
                <div className="flex-1">
                    <p className="text-sm text-gray-800">
                        <span className="font-bold cursor-pointer hover:underline" onClick={() => onProfileClick(notification.fromUser?.id)}>{notification.fromUser?.name}</span> 
                        {type === 'friend' ? ' wants to be your friend.' : ` invited you to join "${notification.group?.name}".`}
                    </p>
                     <div className="mt-3 flex space-x-3">
                        <button onClick={onAccept} className="flex-1 bg-[var(--accent-pink)] text-white font-bold text-xs px-4 py-2 rounded-lg shadow-md hover:opacity-90 transition-opacity">Accept</button>
                        <button onClick={onDecline} className="flex-1 bg-transparent border border-[var(--primary-dark-purple)] text-[var(--primary-dark-purple)] font-bold text-xs px-4 py-2 rounded-lg hover:bg-purple-600 hover:text-white transition-colors">Decline</button>
                    </div>
                </div>
            </div>
        </div>
    );
});

const NotificationsPage: React.FC = () => {
    const { notifications, removeNotification, markAllNotificationsAsRead, markSingleNotificationAsRead, sendDirectNotification } = useAdmin();
    const { userProfile, allUsers, toggleFollow, joinGame } = useAuth();
    const pageContext = useContext(PageContext);
    
    const filteredNotifications = useMemo(() => {
        if (!userProfile) return [];
        
        // 1. Filter out blocked users
        const nonBlockedNotifications = notifications.filter(n => {
            const fromUser = n.fromUser ? allUsers.find(u => u.uid === n.fromUser?.id) : null;

            if (fromUser) {
                const iHaveBlockedThem = userProfile.blockedUsers?.includes(fromUser.uid);
                const theyHaveBlockedMe = fromUser.blockedUsers?.includes(userProfile.uid);
                if (iHaveBlockedThem || theyHaveBlockedMe) return false;
            }
            
            return true;
        });

        // 2. Deduplicate Notifications
        const uniqueMap = new Map<string, Notification>();
        
        nonBlockedNotifications.forEach(n => {
            const fromId = n.fromUser?.id || 'sys';
            const puzzleId = n.puzzle?.id || 'none';
            const groupId = n.group?.id || 'none';
            
            const key = `${n.type}|${fromId}|${puzzleId}|${groupId}`;
            
            if (!uniqueMap.has(key)) {
                uniqueMap.set(key, n);
            }
        });

        return Array.from(uniqueMap.values());

    }, [notifications, userProfile, allUsers]);

    const requests = filteredNotifications.filter(n => n.type === 'friend_request' || n.type === 'group_invite');
    const activity = filteredNotifications.filter(n => n.type !== 'friend_request' && n.type !== 'group_invite');
    
    const handleProfileClick = (userId?: string) => {
        if(userId) {
            pageContext?.switchPage(Page.Profile, 'Profile', { userId });
        }
    }

    const handleAcceptRequest = async (e: React.MouseEvent, notification: Notification) => {
        e.stopPropagation();
        if (!userProfile) return;

        if (notification.type === 'friend_request' && notification.fromUser) {
            toggleFollow(notification.fromUser.id);
            // Send notification back to sender
            sendDirectNotification(notification.fromUser.id, {
                type: 'friend_request_accepted',
                text: 'accepted your friend request.',
                fromUser: {
                    id: userProfile.uid,
                    name: userProfile.displayName,
                    avatar: userProfile.photoURL,
                }
            });
        } else if (notification.type === 'group_invite' && notification.group) {
            await joinGame(notification.group.id);
            pageContext?.switchPage(Page.GameDetail, notification.group.name, { gameId: notification.group.id });
        }

        // Remove all duplicates
        const fromId = notification.fromUser?.id || 'sys';
        const puzzleId = notification.puzzle?.id || 'none';
        const groupId = notification.group?.id || 'none';
        const key = `${notification.type}|${fromId}|${puzzleId}|${groupId}`;

        notifications.forEach(n => {
            const nFromId = n.fromUser?.id || 'sys';
            const nPuzzleId = n.puzzle?.id || 'none';
            const nGroupId = n.group?.id || 'none';
            const nKey = `${n.type}|${nFromId}|${nPuzzleId}|${nGroupId}`;
            
            if (nKey === key) {
                removeNotification(n.id);
            }
        });
    };

    const handleDeclineRequest = (e: React.MouseEvent, notification: Notification) => {
        e.stopPropagation();
        // Remove all duplicates
        const fromId = notification.fromUser?.id || 'sys';
        const puzzleId = notification.puzzle?.id || 'none';
        const groupId = notification.group?.id || 'none';
        const key = `${notification.type}|${fromId}|${puzzleId}|${groupId}`;

        notifications.forEach(n => {
            const nFromId = n.fromUser?.id || 'sys';
            const nPuzzleId = n.puzzle?.id || 'none';
            const nGroupId = n.group?.id || 'none';
            const nKey = `${n.type}|${nFromId}|${nPuzzleId}|${nGroupId}`;
            
            if (nKey === key) {
                removeNotification(n.id);
            }
        });
    };

    const handleNotificationClick = (notification: Notification) => {
        // Find all duplicates of this notification and mark them as read
        const fromId = notification.fromUser?.id || 'sys';
        const puzzleId = notification.puzzle?.id || 'none';
        const groupId = notification.group?.id || 'none';
        const key = `${notification.type}|${fromId}|${puzzleId}|${groupId}`;

        notifications.forEach(n => {
            const nFromId = n.fromUser?.id || 'sys';
            const nPuzzleId = n.puzzle?.id || 'none';
            const nGroupId = n.group?.id || 'none';
            const nKey = `${n.type}|${nFromId}|${nPuzzleId}|${nGroupId}`;
            
            if (nKey === key && !n.read) {
                markSingleNotificationAsRead(n.id);
            }
        });
        
        // Handle clicking a reported post notification (which might be deleted/hidden)
        if (notification.type === 'post_under_review') {
            alert("This post is currently hidden while our team reviews it.");
            return;
        }

        if (notification.puzzle) {
            pageContext?.switchPage(Page.PostDetail, 'Post', { puzzle: notification.puzzle });
        } else if (notification.group) {
            pageContext?.switchPage(Page.GameDetail, notification.group.name, { gameId: notification.group.id });
        } else if (notification.type === 'new_message' && notification.fromUser) {
            const senderProfile = allUsers.find(u => u.uid === notification.fromUser!.id);
            if (senderProfile) {
                pageContext?.switchPage(Page.ChatConversation, senderProfile.displayName, { recipient: senderProfile });
            }
        } else if (notification.fromUser) {
            handleProfileClick(notification.fromUser.id);
        }
    };


    return (
        <div className="space-y-4">
             {requests.length > 0 && (
                <div className="space-y-2">
                    <h3 className="font-bold text-gray-700 px-1">Requests</h3>
                    {requests.map(req => (
                        <RequestItem 
                            key={req.id} 
                            notification={req} 
                            onAccept={(e) => handleAcceptRequest(e, req)} 
                            onDecline={(e) => handleDeclineRequest(e, req)} 
                            onProfileClick={handleProfileClick}
                            type={req.type === 'friend_request' ? 'friend' : 'group'}
                        />
                    ))}
                </div>
            )}
            <div className="space-y-2">
                <div className="flex justify-between items-center px-1">
                    <h3 className="font-bold text-gray-700">Activity</h3>
                    {activity.some(n => !n.read) && (
                         <button onClick={() => markAllNotificationsAsRead()} className="text-sm font-bold text-purple-600 hover:underline">
                            Mark All as Read
                        </button>
                    )}
                </div>
                {activity.length > 0 ? (
                    activity.map(notification => (
                        <NotificationItem 
                            key={notification.id} 
                            notification={notification} 
                            onProfileClick={handleProfileClick} 
                            onNotificationClick={handleNotificationClick}
                        />
                    ))
                ) : (
                    <p className="text-center text-gray-500 py-12">You have no new notifications.</p>
                )}
            </div>
        </div>
    );
};

export default NotificationsPage;